import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useMemo } from "react";
import PageTransition from "../components/PageTransition";
import ScrollReveal from "../components/ScrollReveal";
import Bubbles from "../components/Bubbles";
import JumpingFish from "../components/JumpingFish";
import GstinBlock from "../components/GstinBlock";
import CompanyRegistrationBlock from "../components/CompanyRegistrationBlock";
import FssaiDirectory from "../components/FssaiDirectory";
import Breadcrumbs from "../components/Breadcrumbs";
import useDocumentTitle from "../hooks/useDocumentTitle";
import useStructuredData from "../hooks/useStructuredData";
import { buildLocalBusinessSchema } from "../utils/structuredData";
import { COMPANY_REGISTRATION } from "../data/legalRegistrations";

export default function LegalRegistrations() {
  useDocumentTitle("Legal & Registrations");
  const localBusinessSchema = useMemo(
    () => buildLocalBusinessSchema(window.location.origin, COMPANY_REGISTRATION),
    []
  );
  useStructuredData(localBusinessSchema);
  return (
    <PageTransition>
      <section className="relative overflow-hidden bg-gradient-to-b from-ocean-950 to-ocean-900 pt-32 pb-24 text-center text-foam-50 sm:pt-40 sm:pb-28">
        <Bubbles count={12} />
        <JumpingFish className="left-[10%] bottom-10 hidden sm:block" size={36} delay={0.5} />
        <div className="relative mx-auto max-w-2xl px-6">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-xs font-semibold uppercase tracking-widest text-teal-300"
          >
            Legal
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="mt-3 font-display text-4xl font-bold sm:text-5xl"
          >
            Registrations & Corporate Identity
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mt-4 text-foam-100/70"
          >
            Our tax and corporate registration details, published for anyone verifying who we are.
          </motion.p>
        </div>
      </section>

      <Breadcrumbs />

      <section className="mx-auto max-w-4xl px-6 py-24">
        <ScrollReveal>
          <h2 className="font-display text-2xl font-bold text-ocean-950">Registration Details</h2>
        </ScrollReveal>
        <div className="mt-6 grid gap-6 sm:grid-cols-2">
          <ScrollReveal>
            <GstinBlock />
          </ScrollReveal>
          <ScrollReveal delay={0.08}>
            <CompanyRegistrationBlock />
          </ScrollReveal>
        </div>

        <ScrollReveal delay={0.16} className="mt-14">
          <h2 className="font-display text-2xl font-bold text-ocean-950">Licences</h2>
        </ScrollReveal>
        <ScrollReveal delay={0.2} className="mt-6">
          <FssaiDirectory />
        </ScrollReveal>

        <ScrollReveal delay={0.24} className="mt-10 text-center text-sm text-ocean-800/60">
          Looking for food-safety certifications or our accessibility commitment? See{" "}
          <Link to="/compliance" className="font-semibold text-teal-600 hover:text-teal-700">
            Compliance &amp; Food Safety
          </Link>{" "}
          or our{" "}
          <Link to="/accessibility" className="font-semibold text-teal-600 hover:text-teal-700">
            Accessibility Statement
          </Link>
          .
        </ScrollReveal>
      </section>
    </PageTransition>
  );
}
